/**
 * Geometry helpers for bridging the two coordinate spaces involved in annotating a PDF page.
 *
 * pdf.js renders a page at its *effective* size — it applies the page's /Rotate itself — so the
 * Fabric overlay drawn on top of that canvas lives in a "display space" whose origin is the
 * top-left of the rendered page with y increasing downward, measured in canvas pixels.
 *
 * pdf-lib draws into the page's *unrotated* content space, whose origin is the bottom-left of
 * the page box with y increasing upward, measured in points. It does not compensate for /Rotate.
 *
 * Burning an annotation therefore requires undoing both the rotation and the y-flip, which is
 * what these functions do. They are pure so they can be unit tested without pdf.js, pdf-lib or
 * fabric present.
 */

export type PageRotation = 0 | 90 | 180 | 270;

/** A PDF page box (media/crop) in unrotated user space, origin bottom-left. */
export interface PageBox {
    x: number;
    y: number;
    width: number;
    height: number;
}

/** An offset in display space: +dx is right, +dy is down. */
export interface DisplayOffset {
    dx: number;
    dy: number;
}

/** The subset of a Fabric object this module needs. */
export interface FabricAnchor {
    left?: number;
    top?: number;
    /** Fabric rotation in degrees, clockwise in display space. */
    angle?: number;
}

/**
 * fabric's `Text.prototype._fontSizeMult` (5.3.1). A line's height is
 * `fontSize * lineHeight * _fontSizeMult` and its first baseline sits `fontSize * _fontSizeMult`
 * below the object's top. Used as a fallback when the live fabric value can't be read.
 */
export const FABRIC_FONT_SIZE_MULT = 1.13;

/** pdf.js LETTER_SIZE_MEDIABOX — used when a page has no usable media box. */
const LETTER_SIZE_MEDIA_BOX: PageBox = { x: 0, y: 0, width: 612, height: 792 };

/**
 * Reduce a raw /Rotate value to one of the four quarter turns. Negative and out-of-range angles
 * are wrapped, and anything that is not a multiple of 90 is treated as 0 — which is how pdf.js
 * renders such a page, so the display space we are mapping back from is unrotated.
 */
export function normalizePageRotation(angle: number | null | undefined): PageRotation {
    if (typeof angle !== 'number' || !Number.isFinite(angle)) return 0;

    const wrapped = ((angle % 360) + 360) % 360;
    if (wrapped === 90 || wrapped === 180 || wrapped === 270) return wrapped;
    return 0;
}

/**
 * Put a box into canonical form. pdf-lib derives width/height by subtraction without taking a
 * minimum, so a rect stored in reversed corner order (legal in PDF) arrives with negative
 * dimensions. Returns null when the box is missing or non-numeric.
 */
function normalizeBox(box: PageBox | null | undefined): PageBox | null {
    if (!box) return null;

    const { x, y, width, height } = box;
    if (![x, y, width, height].every((value) => typeof value === 'number' && Number.isFinite(value))) {
        return null;
    }

    return {
        x: Math.min(x, x + width),
        y: Math.min(y, y + height),
        width: Math.abs(width),
        height: Math.abs(height),
    };
}

/**
 * Resolve the box pdf.js actually renders, mirroring its `PDFPageProxy.view`: the intersection of
 * the crop box and the media box, falling back to the media box when the crop box is absent or
 * the intersection is empty.
 *
 * This matters because pdf-lib's `getSize()` reports the *media* box while pdf.js sizes its
 * viewport from the *crop* box. On a cropped page those differ in both origin and extent, and
 * since pdf-lib draws in absolute user space the origin has to be added back in.
 */
export function resolveViewBox(cropBox: PageBox | null | undefined, mediaBox: PageBox | null | undefined): PageBox {
    const media = normalizeBox(mediaBox) ?? LETTER_SIZE_MEDIA_BOX;
    const crop = normalizeBox(cropBox);
    if (!crop) return media;

    const left = Math.max(crop.x, media.x);
    const bottom = Math.max(crop.y, media.y);
    const right = Math.min(crop.x + crop.width, media.x + media.width);
    const top = Math.min(crop.y + crop.height, media.y + media.height);

    if (right <= left || top <= bottom) return media;

    return { x: left, y: bottom, width: right - left, height: top - bottom };
}

/**
 * Resolve the rendered view box straight off a pdf-lib page. Duck-typed on getCropBox/getMediaBox
 * so it stays testable, but both are required: pdf-lib's `PDFPage` always defines them, and a page
 * whose boxes cannot be read is not something to paper over. Letting a genuine parse failure
 * propagate is deliberate — quietly substituting a letter-sized box would burn every note on an A4
 * or legal hardcopy against the wrong page extent, with nothing logged and nothing surfaced.
 */
export function resolvePageViewBox(page: {
    getCropBox: () => PageBox;
    getMediaBox: () => PageBox;
}): PageBox {
    return resolveViewBox(page.getCropBox(), page.getMediaBox());
}

/**
 * Map a point in display space (points from the top-left of the rendered page, y down) to
 * unrotated PDF user space (points from the bottom-left of the page, y up).
 *
 * Derived by inverting the pdf.js `PageViewport` transform. Note that for a quarter-turned page
 * the display axes are swapped relative to the page axes, and that the view-box origin is a
 * plain additive term in every case.
 */
export function displayPointToPdf(
    dx: number,
    dy: number,
    rotation: PageRotation,
    view: PageBox
): { x: number; y: number } {
    switch (rotation) {
        case 90:
            return { x: view.x + dy, y: view.y + dx };
        case 180:
            return { x: view.x + view.width - dx, y: view.y + dy };
        case 270:
            return { x: view.x + view.width - dy, y: view.y + view.height - dx };
        default:
            return { x: view.x + dx, y: view.y + view.height - dy };
    }
}

/**
 * Rotate an object-local offset into display space by a Fabric object's own `angle`. Fabric
 * rotates clockwise about the object's left/top in a y-down space, which is the standard
 * rotation matrix.
 */
export function rotateDisplayOffset(offsetX: number, offsetY: number, angleDegrees: number): DisplayOffset {
    if (!angleDegrees) return { dx: offsetX, dy: offsetY };

    const radians = (angleDegrees * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    return {
        dx: offsetX * cos - offsetY * sin,
        dy: offsetX * sin + offsetY * cos,
    };
}

/**
 * Map an offset expressed in a Fabric object's local frame (canvas pixels, origin at the object's
 * left/top, y down) all the way to unrotated PDF user space.
 *
 * `scale` is the pdf.js viewport scale the overlay canvas was sized at.
 */
export function fabricOffsetToPdf(
    obj: FabricAnchor,
    offsetX: number,
    offsetY: number,
    scale: number,
    rotation: PageRotation,
    view: PageBox
): { x: number; y: number } {
    const offset = rotateDisplayOffset(offsetX, offsetY, obj.angle || 0);
    const dx = ((obj.left || 0) + offset.dx) / scale;
    const dy = ((obj.top || 0) + offset.dy) / scale;

    return displayPointToPdf(dx, dy, rotation, view);
}

/** An annotation's placement on the overlay: top-left corner plus its own clockwise rotation. */
export interface DisplayPlacement {
    left: number;
    top: number;
    angle: number;
}

/**
 * Carry an annotation's placement across a page rotation, so it stays where it sits relative to the
 * hardcopy instead of being discarded.
 *
 * `displayWidth`/`displayHeight` are the overlay dimensions BEFORE the turn. Turning the page
 * clockwise turns the whole rendered image, so a point moves the same way the pixels do, and the
 * annotation's own angle gains the same amount.
 */
export function rotatePlacementForPageTurn(
    placement: { left?: number; top?: number; angle?: number },
    turnDegrees: number,
    displayWidth: number,
    displayHeight: number
): DisplayPlacement {
    const left = placement.left || 0;
    const top = placement.top || 0;
    const angle = placement.angle || 0;
    const turn = normalizePageRotation(turnDegrees);

    const turned = ((angle + turn) % 360 + 360) % 360;

    switch (turn) {
        case 90:
            return { left: displayHeight - top, top: left, angle: turned };
        case 180:
            return { left: displayWidth - left, top: displayHeight - top, angle: turned };
        case 270:
            return { left: top, top: displayWidth - left, angle: turned };
        default:
            return { left, top, angle: turned };
    }
}

/**
 * The rotation to hand pdf-lib, in degrees counter-clockwise, so that content drawn for a page
 * rotated `rotation` degrees clockwise by the viewer reads upright — carrying the annotation's own
 * clockwise `angle` through as well.
 */
export function burnRotationDegrees(rotation: PageRotation, objectAngle: number | null | undefined): number {
    const angle = typeof objectAngle === 'number' && Number.isFinite(objectAngle) ? objectAngle : 0;
    return rotation - angle;
}
